import React, { createContext, useContext, useEffect, useRef, ReactNode, useCallback, useMemo } from "react";
import { useProductDetails } from "../Hooks/useProductDetails";
import { useEventImages } from "../Hooks/useEventImages";
import { EventProductInstance, TraceDataContextType } from "../types";

const TraceDataContext = createContext<TraceDataContextType | null>(null);

interface TraceDataProviderProps {
    children: ReactNode;
}

export function TraceDataProvider({ children }: TraceDataProviderProps) {
    const { productDetails, isProductLoading, fetchProductDetails } = useProductDetails();
    const { eventImages, isEventLoading, fetchEventImages } = useEventImages();

    const observerRef = useRef<IntersectionObserver | null>(null);
    const pendingEvents = useRef<Map<string, string[]>>(new Map()); // eventId -> productInstanceIds
    const fetchedEventIds = useRef<Set<string>>(new Set());
    const debounceTimer = useRef<NodeJS.Timeout | null>(null);
    const registeredElements = useRef<Map<string, { element: HTMLDivElement; productInstances: string[] }>>(new Map());

    const flushPendingFetches = useCallback(() => {
        const pendingEntries = Array.from(pendingEvents.current.entries())
            .filter(([eventId]) => !fetchedEventIds.current.has(eventId));

        if (pendingEntries.length === 0) return;

        const eventIds = pendingEntries.map(([eventId]) => eventId);
        const eventProdInstances: EventProductInstance[] = pendingEntries.map(([eventId, productInstanceIds]) => ({
            eventId,
            productInstanceIds
        }));

        eventIds.forEach(id => fetchedEventIds.current.add(id));
        pendingEvents.current.clear();

        // Batch fetch only event images and product details
        fetchEventImages(eventIds);
        fetchProductDetails(eventProdInstances);
    }, [fetchProductDetails, fetchEventImages]);

    // Create observer on mount
    useEffect(() => {
        observerRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const element = entry.target as HTMLElement;
                        const eventId = element.getAttribute('data-event-id');
                        const productInstances = element.getAttribute('data-product-instances');

                        if (eventId && !fetchedEventIds.current.has(eventId)) {
                            const instanceIds = productInstances ? JSON.parse(productInstances) : [];
                            pendingEvents.current.set(eventId, instanceIds);
                        }
                    }
                });

                // Debounce: batch all visible items before fetching
                if (debounceTimer.current) {
                    clearTimeout(debounceTimer.current);
                }
                debounceTimer.current = setTimeout(flushPendingFetches, 100);
            },
            { threshold: 0.1, rootMargin: "100px" }
        );

        // Observe any elements that were registered before observer was ready
        registeredElements.current.forEach(({ element, productInstances }, eventId) => {
            element.setAttribute('data-event-id', eventId);
            element.setAttribute('data-product-instances', JSON.stringify(productInstances || []));
            observerRef.current!.observe(element);
        });

        return () => {
            if (observerRef.current) {
                observerRef.current.disconnect();
            }
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [flushPendingFetches]);

    // Function for TimeLine components to register their DOM elements
    const registerRef = useCallback((eventId: string, outputProductInstances: string[], element: HTMLDivElement | null) => {
        if (!element) return;

        // Store element for later observation if observer not ready yet
        registeredElements.current.set(eventId, { element, productInstances: outputProductInstances });

        if (observerRef.current) {
            element.setAttribute('data-event-id', eventId);
            element.setAttribute('data-product-instances', JSON.stringify(outputProductInstances || []));
            observerRef.current.observe(element);
        }
    }, []);

    const value: TraceDataContextType = useMemo(() => ({
        productDetails,
        isProductLoading,
        eventImages,
        isEventLoading,
        registerRef,
    }), [
        productDetails,
        isProductLoading,
        eventImages,
        isEventLoading,
        registerRef,
    ]);

    return (
        <TraceDataContext.Provider value={value}>
            {children}
        </TraceDataContext.Provider>
    );
}

export function useTraceData(): TraceDataContextType {
    const context = useContext(TraceDataContext);
    if (!context) {
        throw new Error("useTraceData must be used within a TraceDataProvider");
    }
    return context;
}
